import { CModal, CModalBody } from '@coreui/react'
import CloseIcon from '@mui/icons-material/Close'
import DownloadIcon from '@mui/icons-material/Download'
import { IconButton } from '@mui/material'
import { noop } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TranslateHTML from 'src/components/Translation/Translation'
import { downloadInvoice } from 'src/views/Invoices/Invoices'
import SelectDropdown from '../SelectDropdown/SelectDropdown'
import styles from './multiReportComponent.module.scss'

const ReportTypes = Object.freeze({
  MultiAccountInvoiceSummary: 'reports.multiAccount.invoiceSummary',
  MultiAccountSnapshot: 'reports.multiAccount.snapshot',
})

const getYears = (startDate, currentDate) => {
  const years = new Set()
  const createdDate = startDate.clone()
  while (true) {
    const createdYear = createdDate.get('year')
    const currentYear = currentDate.get('year')
    if (createdYear > currentYear) {
      break
    }
    years.add(createdYear)
    createdDate.add(1, 'year')
  }
  return Array.from(years)
}
const getMonths = (startDate, endDate, selectedYear) => {
  const months = new Set()
  let createdDate = startDate.clone()
  let currentDate = endDate.clone()
  if (currentDate.format('YYYY') === selectedYear) {
    createdDate = currentDate.clone().startOf('year')
  } else if (createdDate.format('YYYY') === selectedYear) {
    currentDate = moment(selectedYear, 'YYYY').endOf('year')
  } else {
    createdDate = moment(selectedYear, 'YYYY').startOf('year')
    currentDate = moment(selectedYear, 'YYYY').endOf('year')
  }
  while (true) {
    if (createdDate.isAfter(currentDate)) {
      break
    }
    months.add(createdDate.format('MMMM'))
    createdDate.add(1, 'month')
  }
  return Array.from(months)
}
const getMonthsAndYears = (accountCreatedDate = '', selectedDates) => {
  const { year: selectedYear } = selectedDates
  if (!accountCreatedDate) {
    const date = moment()
    const year = date.format('YYYY')
    const month = date.format('MMMM')
    return [[month], [year]]
  }
  const createdDate = moment(accountCreatedDate)
  const currentDate = moment()
  const selectableYears = getYears(createdDate, currentDate)
  const selectableMonths = getMonths(createdDate, currentDate, selectedYear)
  return [selectableMonths, selectableYears]
}
const getAccountName = (contactList, currentAccount) => {
  if (!currentAccount && !contactList?.length) {
    return ''
  }
  const { SecondaryContact } = currentAccount
  const contact = contactList.find((contact) => contact.id === SecondaryContact)
  if (!contact) {
    return ''
  }
  return `${contact.firstname} ${contact.lastname}`
}
const MultiReportComponent = ({
  viewAccount,
  contactList,
  openMultiReport,
  setOpenMultiReport,
  setAlertComponent,
  setLoading,
}) => {
  const { t } = useTranslation()
  const { date_joined = '', createdtime = '' } = viewAccount
  const currentDate = moment()
  const [form, setForm] = useState({
    month: currentDate.format('MMMM'),
    year: currentDate.format('YYYY'),
  })
  const [requested, setRequested] = useState({
    isRequested: false,
    requestedMonth: '',
    requestedYear: '',
  })
  const [reportType, setReportType] = useState(ReportTypes.MultiAccountInvoiceSummary)
  const [month, year] = getMonthsAndYears(date_joined || createdtime, form)
  const buildDates = () => {
    const start = moment(
      `${requested.requestedYear} ${requested.requestedMonth} 01`,
      'YYYY MMMM DD',
    )
    const end = moment(
      `${requested.requestedYear} ${requested.requestedMonth} 01`,
      'YYYY MMMM DD',
    ).endOf('month')
    return { start, end }
  }
  useEffect(() => {
    if (form.month === requested.requestedMonth && form.year === requested.requestedYear) {
      return
    }
    setRequested({
      isRequested: true,
      requestedMonth: form.month,
      requestedYear: form.year,
    })
  }, [form, reportType])
  const isPreBill = viewAccount?.fo_billing_period !== 'Only after delivery'
  const onClickDownload = downloadInvoice(
    setAlertComponent,
    setLoading,
    viewAccount?.fruitful_account_id,
  )
  const availableReport = () => {
    const { isRequested, requestedMonth, requestedYear } = requested
    if (!isRequested && !reportType) {
      return <></>
    }
    return (
      <div className={styles.generatedReportContainerDiv}>
        <div>{t('reports.multiAccount.generatedReport')}</div>
        <div className={styles.reportContainerDiv}>
          <div>
            {t(reportType)}{' '}
            {reportType !== ReportTypes.MultiAccountSnapshot && (
              <div>
                <span className={styles.reportYearMonth}>
                  {requestedMonth} {requestedYear}
                </span>
              </div>
            )}
          </div>
          <IconButton
            title={t('reports.multiAccount.downloadReport')}
            onClick={() =>
              onClickDownload(
                '',
                '',
                isPreBill,
                false,
                true,
                reportType === ReportTypes.MultiAccountSnapshot,
                buildDates(),
              )
            }
          >
            <DownloadIcon />
          </IconButton>
        </div>
      </div>
    )
  }

  const handleClose = () => {
    setOpenMultiReport(false)
  }

  return (
    <CModal alignment="center" visible={openMultiReport} onClose={handleClose}>
      <CModalBody className={styles.modalStyle}>
        <div className={styles.headerContainerDiv}>
          <div className={styles.headerTitleDiv}>
            <div>{t('reports.multiAccount.title')}</div>
            <IconButton title={t('reports.multiAccount.close')} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={styles.headerNameDiv}>{getAccountName(contactList, viewAccount)}</div>
        </div>
        <div className={styles.descriptionContainerDiv}>
          <div>
            <TranslateHTML
              translationKey="reports.multiAccount.reportDescription"
              components={[<b key="accounts" />]}
            />
          </div>
        </div>
        <div className={styles.formContainerDiv}>
          <div className={styles.singleFieldContainer}>
            <SelectDropdown
              helperText={t('reports.multiAccount.reportType')}
              helperTextStyle={styles.helperTextStyle}
              name={'reportType'}
              options={Object.values(ReportTypes).map((type) => t(type))}
              value={t(reportType)}
              handleChange={(e) => {
                e.preventDefault()
                const translatedValue = e.target.value
                // Find the key that matches the translated value
                const key = Object.values(ReportTypes).find((type) => t(type) === translatedValue)
                setReportType(key || ReportTypes.MultiAccountInvoiceSummary)
              }}
            />
          </div>
          <div className={styles.descriptionContainerDiv} style={{ margin: '10px 0px' }}>
            {t('reports.multiAccount.pickMonthYear')}
          </div>
          <div className={styles.doubleFieldContainer}>
            <SelectDropdown
              isDisabled={reportType === ReportTypes.MultiAccountSnapshot}
              helperText={t('reports.multiAccount.month')}
              helperTextStyle={styles.helperTextStyle}
              name={'month'}
              options={month}
              value={form.month}
              handleChange={(e) => {
                e.preventDefault()
                const { name, value } = e.target
                setForm((currentValues) => ({
                  ...currentValues,
                  [name]: value,
                }))
              }}
            />
            <SelectDropdown
              isDisabled={reportType === ReportTypes.MultiAccountSnapshot}
              helperText={t('reports.multiAccount.year')}
              helperTextStyle={styles.helperTextStyle}
              name={'year'}
              options={year}
              value={form.year}
              handleChange={(e) => {
                e.preventDefault()
                const { name, value } = e.target
                setForm((currentValues) => ({
                  ...currentValues,
                  [name]: value,
                }))
              }}
            />
          </div>
        </div>
        {availableReport()}
      </CModalBody>
    </CModal>
  )
}
MultiReportComponent.defaultProps = {
  viewAccount: {},
  contactList: [],
  openMultiReport: false,
  setOpenMultiReport: noop,
  setAlertComponent: noop,
  setLoading: noop,
}
MultiReportComponent.propTypes = {
  viewAccount: PropTypes.object,
  contactList: PropTypes.array,
  openMultiReport: PropTypes.bool,
  setOpenMultiReport: PropTypes.func,
  setAlertComponent: PropTypes.func,
  setLoading: PropTypes.func,
}
export default MultiReportComponent
